import React, { useEffect, useState } from "react";
import { api, formatError, absoluteUrl } from "@/api/client";
import { useConfirm } from "@/context/ConfirmContext";
import { printHtml } from "@/lib/printDoc";
import { toast } from "sonner";
import { X, Sparkle, Printer, CheckCircle, Trash, ArrowsClockwise, Plus, ClockCounterClockwise } from "@phosphor-icons/react";
import { TrackerImg } from "@/components/TrackerPhoto";
import { DatePicker } from "@/components/DatePicker";

const PRIORITY_COLOR = { High: "#dc2626", Medium: "#eab308", Low: "#16a34a" };

function ProgressRing({ percent, size = 76 }) {
  const r = (size - 10) / 2, c = 2 * Math.PI * r, p = Math.max(0, Math.min(100, percent || 0));
  // Percent label is an HTML overlay (not SVG <text>) so dev tooling that wraps JSX expressions in <span> can't hide it
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="report-progress-ring">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e5e5" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#171717" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${(c * p) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-bold text-neutral-900" style={{ fontSize: size * 0.22 }} data-testid="report-progress-ring-label">{p}%</div>
    </div>
  );
}

function ringSvg(percent, size = 96) {
  const r = (size - 12) / 2, c = 2 * Math.PI * r, p = Math.max(0, Math.min(100, percent || 0));
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="#e5e5e5" stroke-width="10"/><circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="#171717" stroke-width="10" stroke-linecap="round" stroke-dasharray="${(c * p) / 100} ${c}" transform="rotate(-90 ${size / 2} ${size / 2})"/><text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" font-size="${size * 0.22}" font-weight="700" fill="#171717">${p}%</text></svg>`;
}

const OBS_FIELDS = [
  ["completed", "Work Completed"], ["in_progress", "Work In Progress"], ["pending", "Pending Work"],
  ["issues", "Issues / Defects / Concerns"], ["dependencies", "Dependencies / Delays"], ["approvals_required", "Decisions / Approvals Required"],
];

async function photoToDataUrl(photoId) {
  try {
    const res = await api.get(`/tracker/photos/${photoId}/file`, { responseType: "blob" });
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(res.data);
    });
  } catch { return ""; }
}

export default function TrackerReportModal({ mode, reportId, project, tower, activities, photos, canEdit, onClose, onSaved, onChanged }) {
  const [phase, setPhase] = useState(mode === "generate" ? "form" : "loading");
  const [genForm, setGenForm] = useState({
    visit_date: new Date().toISOString().slice(0, 10), prepared_by: "",
    observations: { completed: "", in_progress: "", pending: "", issues: "", dependencies: "", approvals_required: "" },
    activity_ids: activities.filter((a) => a.is_applicable).map((a) => a.id),
    photo_ids: [],
  });
  const [report, setReport] = useState(null);
  const [versions, setVersions] = useState([]);
  const [content, setContent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const loadReport = async (id) => {
    setPhase("loading");
    try {
      const { data } = await api.get(`/tracker/reports/${id}`);
      setReport(data.report);
      setVersions(data.versions);
      setContent(data.report.content);
      setDirty(false);
      setPhase("preview");
    } catch (err) { toast.error(formatError(err)); onClose(); }
  };
  useEffect(() => { if (mode === "view" && reportId) loadReport(reportId); }, [mode, reportId]); // eslint-disable-line

  const toggleActivity = (id) => setGenForm((f) => ({ ...f, activity_ids: f.activity_ids.includes(id) ? f.activity_ids.filter((x) => x !== id) : [...f.activity_ids, id] }));
  const togglePhoto = (id) => setGenForm((f) => ({ ...f, photo_ids: f.photo_ids.includes(id) ? f.photo_ids.filter((x) => x !== id) : [...f.photo_ids, id] }));

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/tracker/reports/generate", { project_id: project.id, tower_id: tower?.id || "", ...genForm });
      setReport(data); setVersions([{ id: data.id, version: data.version, status: data.status }]); setContent(data.content); setDirty(false);
      setPhase("preview");
      if (data.content?.ai_failed) toast.warning("AI text could not be generated right now — the report was created with placeholder text. You can edit it manually or try Regenerate.");
      else toast.success("Report generated — review before approving");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tracker/reports/${report.id}/regenerate`);
      setContent(data.content); setReport(data); setDirty(false);
      if (data.content?.ai_failed) toast.warning("AI text could not be generated right now — try again shortly.");
      else toast.success("AI narrative regenerated");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/tracker/reports/${report.id}`, { content });
      setReport(data); setDirty(false);
      toast.success("Draft saved");
      onChanged && onChanged();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    const ok = await confirm({ title: "Finalise this report?", message: "Once finalised, this version is locked and preserved as a historical record. Any later corrections will create a new version.", confirmLabel: "Finalise", tone: "default" });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tracker/reports/${report.id}/approve`);
      setReport(data); setContent(data.content);
      toast.success("Report finalised");
      onChanged && onChanged();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const newVersion = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tracker/reports/${report.id}/new-version`);
      await loadReport(data.id);
      toast.success(`Version ${data.version} created — edit and finalise when ready`);
      onChanged && onChanged();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const deleteDraft = async () => {
    const ok = await confirm({ title: "Delete this draft?", message: "This cannot be undone.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/tracker/reports/${report.id}`); toast.success("Draft deleted"); onSaved && onSaved(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const photoImgs = await Promise.all((content.photos || []).map(async (p) => ({ ...p, dataUrl: await photoToDataUrl(p.photo_id) })));
      printHtml(buildSiteReportHtml(content, photoImgs), { title: `Site Visit Report — ${content.header.project_name}${content.header.tower_name ? ` — ${content.header.tower_name}` : ""} — ${content.header.visit_date}`, pageSize: "A4" });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-3xl my-8 fade-in" data-testid="tracker-report-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div className="font-display font-semibold text-neutral-900 flex items-center gap-2"><Sparkle size={16} /> {phase === "form" ? `Generate Site Visit Report${tower ? ` — ${tower.name}` : ""}` : "Site Visit Report"}</div>
          <button onClick={onClose} data-testid="report-modal-close"><X size={16} /></button>
        </div>

        {phase === "loading" && <div className="p-12 text-center text-neutral-500">Loading…</div>}

        {phase === "form" && (
          <form onSubmit={generate} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-neutral-500 mb-1 block">Site Visit Date</label><DatePicker testId="report-visit-date" value={genForm.visit_date} onChange={(v) => setGenForm({ ...genForm, visit_date: v })} /></div>
              <div><label className="text-xs text-neutral-500 mb-1 block">Prepared By</label><input placeholder="Your name" value={genForm.prepared_by} onChange={(e) => setGenForm({ ...genForm, prepared_by: e.target.value })} className="dos-input" /></div>
            </div>
            <div>
              <label className="text-xs text-neutral-500 mb-1.5 block">Relevant Activities ({genForm.activity_ids.length} selected)</label>
              <div className="border border-neutral-200 rounded-md max-h-32 overflow-y-auto p-2 grid grid-cols-2 gap-1">
                {activities.map((a) => (
                  <label key={a.id} className="flex items-center gap-1.5 text-xs cursor-pointer p-1 hover:bg-neutral-50 rounded">
                    <input type="checkbox" checked={genForm.activity_ids.includes(a.id)} onChange={() => toggleActivity(a.id)} /> {a.name} <span className="text-neutral-400">({a.completion_percent}%)</span>
                  </label>
                ))}
              </div>
            </div>
            {OBS_FIELDS.map(([key, label]) => (
              <div key={key}>
                <label className="text-xs text-neutral-500 mb-1 block">{label}</label>
                <textarea data-testid={`obs-${key}`} value={genForm.observations[key]} onChange={(e) => setGenForm({ ...genForm, observations: { ...genForm.observations, [key]: e.target.value } })} className="dos-input" rows={2} placeholder="Leave blank if not applicable" />
              </div>
            ))}
            {photos.length > 0 && (
              <div>
                <label className="text-xs text-neutral-500 mb-1.5 block">Include Photographs ({genForm.photo_ids.length} selected)</label>
                <div className="grid grid-cols-6 gap-2 max-h-28 overflow-y-auto">
                  {photos.map((p) => (
                    <button type="button" key={p.id} onClick={() => togglePhoto(p.id)} className={`aspect-square rounded border-2 overflow-hidden ${genForm.photo_ids.includes(p.id) ? "border-neutral-900" : "border-transparent"}`}>
                      <TrackerImg photoId={p.id} className="w-full h-full object-cover opacity-80" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
              <button data-testid="report-generate-submit" type="submit" disabled={busy} className="dos-btn dos-btn-primary"><Sparkle size={14} /> {busy ? "Generating…" : "Generate Site Visit Report"}</button>
            </div>
          </form>
        )}

        {phase === "preview" && content && (
          <div className="p-5 space-y-5">
            {versions.length > 1 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <ClockCounterClockwise size={14} className="text-neutral-400" />
                {versions.map((v) => (
                  <button key={v.id} onClick={() => loadReport(v.id)} className={`px-2 py-1 text-xs rounded ${v.id === report.id ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600"}`}>v{v.version} {v.status === "finalized" ? "✓" : ""}</button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className={`dos-badge ${report.status === "finalized" ? "bg-emerald-50 text-emerald-700" : report.status === "pending_review" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-600"}`}>{report.status === "pending_review" ? "Pending Review" : report.status === "finalized" ? "Finalised" : "Draft"}</span>
            </div>

            <div className="dos-card p-4">
              <div className="grid grid-cols-2 gap-2 text-xs text-neutral-600">
                <div><b>Project:</b> {content.header.project_name}</div>
                {content.header.tower_name && <div data-testid="report-tower-name"><b>Tower / Wing:</b> {content.header.tower_name}</div>}
                <div><b>Client:</b> {content.header.client_name}</div>
                <div><b>Location:</b> {content.header.location}</div>
                <div><b>Visit Date:</b> {content.header.visit_date}</div>
                <div><b>Prepared By:</b> {content.header.prepared_by}</div>
                <div><b>Generated:</b> {content.header.generated_date}</div>
              </div>
            </div>

            <Section title="Overall Progress">
              <div className="flex items-start gap-4">
                <ProgressRing percent={content.overall.percent} />
                <div className="flex-1 space-y-2">
                  <EditableText label="Summary" value={content.overall.summary} editable={canEdit && report.status !== "finalized"} onChange={(v) => { setContent({ ...content, overall: { ...content.overall, summary: v } }); setDirty(true); }} />
                  <EditableText label="Key Developments Since Previous Visit" value={content.overall.key_developments} editable={canEdit && report.status !== "finalized"} onChange={(v) => { setContent({ ...content, overall: { ...content.overall, key_developments: v } }); setDirty(true); }} />
                </div>
              </div>
            </Section>

            <Section title="Activity-Wise Progress">
              <table className="w-full text-xs">
                <thead><tr className="text-neutral-500 border-b border-neutral-200"><th className="text-left py-1.5 pr-3">Activity</th><th className="text-right py-1.5 px-3">%</th><th className="text-left py-1.5 px-3">Status</th><th className="text-left py-1.5 pl-3">Remark</th></tr></thead>
                <tbody>
                  {content.activities.map((a, i) => (
                    <tr key={i} className="border-b border-neutral-100 last:border-0">
                      <td className="py-1.5 pr-3">{a.name}</td><td className="py-1.5 px-3 text-right font-mono">{a.percent}%</td><td className="py-1.5 px-3">{a.status}</td><td className="py-1.5 pl-3 text-neutral-500">{a.remark || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            <Section title="Site Observations">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OBS_FIELDS.map(([key, label]) => (
                  <EditableText key={key} label={label} value={content.observations[key]} editable={canEdit && report.status !== "finalized"} onChange={(v) => { setContent({ ...content, observations: { ...content.observations, [key]: v } }); setDirty(true); }} />
                ))}
              </div>
            </Section>

            {content.photos.length > 0 && (
              <Section title="Photographic Documentation">
                <div className="grid grid-cols-4 gap-2">
                  {content.photos.map((p, i) => (
                    <div key={i} className="relative">
                      <TrackerImg photoId={p.photo_id} className="w-full aspect-square object-cover rounded" />
                      {canEdit && report.status !== "finalized" && (
                        <button onClick={() => { setContent({ ...content, photos: content.photos.filter((_, idx) => idx !== i) }); setDirty(true); }} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5"><X size={10} /></button>
                      )}
                      {p.caption && <div className="text-[10px] text-neutral-500 mt-0.5 truncate">{p.caption}</div>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <Section title="Action Items">
              <ActionItemsTable items={content.action_items} editable={canEdit && report.status !== "finalized"} onChange={(items) => { setContent({ ...content, action_items: items }); setDirty(true); }} />
            </Section>

            <Section title="Next Steps">
              <EditableText value={content.next_steps} editable={canEdit && report.status !== "finalized"} onChange={(v) => { setContent({ ...content, next_steps: v }); setDirty(true); }} />
            </Section>

            <div className="text-[11px] text-neutral-400 italic border-t border-neutral-100 pt-3">{content.disclaimer}</div>

            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-neutral-200">
              {canEdit && report.status !== "finalized" && <button data-testid="report-delete-draft" onClick={deleteDraft} className="dos-btn dos-btn-secondary text-xs text-red-500"><Trash size={12} /> Delete Draft</button>}
              {canEdit && report.status !== "finalized" && <button data-testid="report-regenerate" onClick={regenerate} disabled={busy} className="dos-btn dos-btn-secondary text-xs"><ArrowsClockwise size={12} /> Regenerate AI Text</button>}
              {canEdit && report.status !== "finalized" && dirty && <button data-testid="report-save-draft" onClick={saveDraft} disabled={busy} className="dos-btn dos-btn-secondary text-xs">Save Draft</button>}
              <button data-testid="report-download-pdf" onClick={downloadPdf} disabled={busy} className="dos-btn dos-btn-secondary text-xs"><Printer size={12} /> Download PDF</button>
              {canEdit && report.status === "finalized" && <button data-testid="report-new-version" onClick={newVersion} disabled={busy} className="dos-btn dos-btn-secondary text-xs"><Plus size={12} /> Create New Version</button>}
              {canEdit && report.status !== "finalized" && <button data-testid="report-approve" onClick={approve} disabled={busy} className="dos-btn dos-btn-primary text-xs"><CheckCircle size={12} /> Approve &amp; Finalise</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-sm font-bold text-neutral-900 mb-2">{title}</div>
      {children}
    </div>
  );
}

function EditableText({ label, value, editable, onChange }) {
  return (
    <div>
      {label && <div className="text-xs font-bold text-neutral-800 mb-0.5">{label}</div>}
      {editable ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="dos-input text-sm" rows={2} />
      ) : (
        <div className="text-sm text-neutral-700 whitespace-pre-line">{value || "Not provided"}</div>
      )}
    </div>
  );
}

const AI_COLS = "grid grid-cols-1 sm:grid-cols-[1fr_8rem_8.5rem_6.5rem_1.5rem] gap-2 items-center";
const fmtDate = (d) => (/^\d{4}-\d{2}-\d{2}$/.test(d || "") ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Not provided");

function ActionItemsTable({ items, editable, onChange }) {
  const update = (i, field, v) => { const next = [...items]; next[i] = { ...next[i], [field]: v }; onChange(next); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { action: "", responsible: "", target_date: "", priority: "Medium" }]);
  if (!items.length && !editable) return <div className="text-sm text-neutral-500">No action items.</div>;
  return (
    <div className="space-y-2" data-testid="action-items-table">
      {items.length > 0 && (
        <div className={`${AI_COLS} hidden sm:grid px-2 text-xs font-bold text-neutral-800`} data-testid="action-items-header">
          <span>Description</span><span>Responsible</span><span>Date</span><span>Priority</span><span />
        </div>
      )}
      {items.map((it, i) => (
        <div key={i} className={`bg-neutral-50 rounded-md p-2 ${AI_COLS}`} data-testid={`action-item-row-${i}`}>
          {editable ? (
            <>
              <input value={it.action} onChange={(e) => update(i, "action", e.target.value)} placeholder="Description" data-testid={`action-desc-${i}`} className="dos-input py-1 text-xs" />
              <input value={it.responsible === "Not provided" ? "" : it.responsible} onChange={(e) => update(i, "responsible", e.target.value)} placeholder="Responsible" data-testid={`action-responsible-${i}`} className="dos-input py-1 text-xs" />
              <DatePicker value={/^\d{4}-\d{2}-\d{2}$/.test(it.target_date || "") ? it.target_date : ""} onChange={(v) => update(i, "target_date", v)} placeholder="Select date" testId={`action-date-${i}`} className="py-1 text-xs" />
              <select value={it.priority} onChange={(e) => update(i, "priority", e.target.value)} data-testid={`action-priority-select-${i}`} style={{ color: PRIORITY_COLOR[it.priority], fontWeight: 700 }} className="dos-input py-1 text-xs"><option>High</option><option>Medium</option><option>Low</option></select>
              <button onClick={() => remove(i)} data-testid={`action-remove-${i}`} className="p-1 text-neutral-400 hover:text-red-500"><Trash size={12} /></button>
            </>
          ) : (
            <>
              <span className="text-xs font-medium text-neutral-900">{it.action}</span>
              <span className="text-xs text-neutral-600">{it.responsible || "Not provided"}</span>
              <span className="text-xs text-neutral-600">{fmtDate(it.target_date)}</span>
              <b className="text-xs" data-testid={`action-priority-${i}`} style={{ color: PRIORITY_COLOR[it.priority] || "#525252" }}>{it.priority}</b>
              <span />
            </>
          )}
        </div>
      ))}
      {editable && <button onClick={add} data-testid="action-add-btn" className="dos-btn dos-btn-secondary text-xs"><Plus size={12} /> Add Action Item</button>}
    </div>
  );
}

function buildSiteReportHtml(c, photoImgs) {
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);
  const actRows = c.activities.map((a) => `<tr><td>${esc(a.name)}</td><td class="num">${a.percent}%</td><td>${esc(a.status)}</td><td>${esc(a.remark || "—")}</td></tr>`).join("");
  const obsBlock = (label, v) => `<div style="margin-bottom:8px;"><div style="font-size:11px;font-weight:700;color:#171717;">${esc(label)}</div><div style="font-size:12px;">${esc(v || "Not provided")}</div></div>`;
  const actionRows = (c.action_items || []).map((a) => `<tr><td>${esc(a.action)}</td><td>${esc(a.responsible || "Not provided")}</td><td>${esc(fmtDate(a.target_date))}</td><td style="font-weight:700;color:${PRIORITY_COLOR[a.priority] || "#525252"};">${esc(a.priority || "Medium")}</td></tr>`).join("");
  const H = (t) => `<div style="font-size:13px;font-weight:700;color:#171717;margin-bottom:6px;">${t}</div>`;
  const photosBlock = photoImgs.filter((p) => p.dataUrl).map((p) => `<div style="display:inline-block;width:23%;margin:1%;vertical-align:top;"><img src="${p.dataUrl}" style="width:100%;border-radius:4px;" /><div style="font-size:9px;color:#737373;margin-top:2px;">${esc(p.caption || "")}${p.activity_name ? " · " + esc(p.activity_name) : ""}</div></div>`).join("");
  return `
    <div class="section" style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #171717;padding-bottom:12px;margin-bottom:16px;">
      <div style="display:flex;align-items:center;gap:10px;">
        ${c.header.company_logo_url ? `<img src="${esc(absoluteUrl(c.header.company_logo_url))}" style="width:40px;height:40px;object-fit:contain;border-radius:4px;" />` : ""}
        <div>
          <div class="text-xl font-bold">${esc(c.header.company_name)}</div>
          ${c.header.company_tagline ? `<div style="font-size:10px;color:#737373;text-transform:uppercase;letter-spacing:.05em;">${esc(c.header.company_tagline)}</div>` : ""}
          <div class="text-sm font-bold" style="margin-top:4px;">Site Visit Report</div>
        </div>
      </div>
      <div class="text-right text-xs text-neutral-600">
        <div><b>Project:</b> ${esc(c.header.project_name)}</div>
        ${c.header.tower_name ? `<div><b>Tower / Wing:</b> ${esc(c.header.tower_name)}</div>` : ""}
        <div><b>Client:</b> ${esc(c.header.client_name)}</div>
        <div><b>Location:</b> ${esc(c.header.location)}</div>
        <div><b>Visit Date:</b> ${esc(c.header.visit_date)}</div>
        <div><b>Generated:</b> ${esc(c.header.generated_date)}</div>
        <div><b>Prepared By:</b> ${esc(c.header.prepared_by)}</div>
      </div>
    </div>
    <div class="section" style="margin-bottom:16px;">
      ${H("Overall Progress")}
      <div style="display:flex;gap:16px;align-items:center;">
        <div style="flex-shrink:0;">${ringSvg(c.overall.percent)}</div>
        <div style="flex:1;">
          <div class="text-sm" style="margin-bottom:6px;">${esc(c.overall.summary)}</div>
          <div class="text-xs text-neutral-600"><b>Key developments:</b> ${esc(c.overall.key_developments)}</div>
        </div>
      </div>
    </div>
    <div class="section" style="margin-bottom:16px;">
      ${H("Activity-Wise Progress")}
      <table class="items" style="width:100%;"><thead><tr><th>Activity</th><th class="num">%</th><th>Status</th><th>Remark</th></tr></thead><tbody>${actRows}</tbody></table>
    </div>
    <div class="section" style="margin-bottom:16px;">
      ${H("Site Observations")}
      ${obsBlock("Work Completed", c.observations.completed)}
      ${obsBlock("Work In Progress", c.observations.in_progress)}
      ${obsBlock("Pending Work", c.observations.pending)}
      ${obsBlock("Issues / Defects / Concerns", c.observations.issues)}
      ${obsBlock("Dependencies / Delays", c.observations.dependencies)}
      ${obsBlock("Decisions / Approvals Required", c.observations.approvals_required)}
    </div>
    ${photoImgs.length ? `<div class="section" style="margin-bottom:16px;">${H("Photographic Documentation")}${photosBlock}</div>` : ""}
    <div class="section" style="margin-bottom:16px;">
      ${H("Action Items")}
      <table class="items" style="width:100%;"><thead><tr><th>Description</th><th>Responsible</th><th>Date</th><th>Priority</th></tr></thead><tbody>${actionRows || '<tr><td colspan="4">None</td></tr>'}</tbody></table>
    </div>
    <div class="section" style="margin-bottom:16px;">
      ${H("Next Steps")}
      <div class="text-sm">${esc(c.next_steps)}</div>
    </div>
    <div class="section text-xs text-neutral-500" style="border-top:1px solid #d4d4d4;padding-top:8px;font-style:italic;">${esc(c.disclaimer)}</div>
  `;
}
